import React, { useState } from 'react';

import {
  MinusOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { Alert, Button, InputNumber, Spin as SpinAntD, Switch } from 'antd';

import { AlgorithmResult, CompareBanner } from './AlgorithmResult';
import {
  chargeBoxesWithoutPhase,
  emptyMeter,
  exampleScenario,
  GRID_PHASES,
  GridPhase,
  newChargeBox,
  SimChargeBox,
  SimScenario,
} from './scenario';
import { SimulatorTour } from './SimulatorTour';
import { StationEditor } from './StationEditor';
import * as S from './styled';
import { useSmartChargingSimulation } from './useSmartChargingSimulation';

// Os tipos do antd 4.10 não declaram children no Spin com o @types/react daqui
const Spin: any = SpinAntD;

const MAX_STATIONS = 24;

type SmartChargingSimulatorProps = {
  /** Cenário de partida (grupo ou estações do formulário). Sem ele, abre no exemplo. */
  initialScenario?: SimScenario | null;
  /** Nome do que foi carregado, para o botão de voltar ao ponto de partida. */
  sourceLabel?: string;
};

type StepperProps = {
  label?: string;
  value: number;
  step: number;
  big?: boolean;
  ariaLabel: string;
  onChange: (value: number) => void;
};

const Stepper = ({ label, value, step, big, ariaLabel, onChange }: StepperProps) => (
  <S.Stepper>
    {label && <S.StepperLabel>{label}</S.StepperLabel>}
    <Button
      shape='circle'
      icon={<MinusOutlined />}
      aria-label={`Diminuir ${ariaLabel}`}
      disabled={value <= 0}
      onClick={() => onChange(Math.max(0, value - step))}
    />
    <S.StepperValue $big={big}>{value} A</S.StepperValue>
    <Button
      shape='circle'
      icon={<PlusOutlined />}
      aria-label={`Aumentar ${ariaLabel}`}
      onClick={() => onChange(value + step)}
    />
  </S.Stepper>
);

export const SmartChargingSimulator = ({ initialScenario, sourceLabel }: SmartChargingSimulatorProps) => {
  const start = () => initialScenario || exampleScenario();
  const [scenario, setScenario] = useState<SimScenario>(start);
  const [tourOpen, setTourOpen] = useState(false);
  const { result, loading, error } = useSmartChargingSimulation(scenario);

  const update = (patch: Partial<SimScenario>) => setScenario((current) => ({ ...current, ...patch }));

  const updateChargeBox = (key: string, chargeBox: SimChargeBox) =>
    update({
      charge_boxes: scenario.charge_boxes.map((current) => (current.key === key ? chargeBox : current)),
    });

  const setExternalLoad = (phase: GridPhase, value: number) =>
    scenario.meter &&
    update({
      meter: { ...scenario.meter, external_load_a: { ...scenario.meter.external_load_a, [phase]: value } },
    });

  const withoutPhase = chargeBoxesWithoutPhase(scenario.charge_boxes);
  const chargingCount = scenario.charge_boxes.reduce(
    (total, chargeBox) => total + chargeBox.connectors.filter((connector) => connector.charging).length,
    0,
  );

  return (
    <S.Page>
      <SimulatorTour run={tourOpen} onClose={() => setTourOpen(false)} />

      <S.Toolbar>
        <S.ToolbarText>
          Monte um cenário e compare como o Geral e o Por Fases dividem a corrente. O cálculo é o mesmo
          do grupo de verdade, mas nada aqui é gravado nem enviado para as estações.
        </S.ToolbarText>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {initialScenario && (
            <Button icon={<ReloadOutlined />} onClick={() => setScenario(initialScenario)}>
              {sourceLabel || 'Recarregar o grupo'}
            </Button>
          )}
          <Button onClick={() => setScenario(exampleScenario())}>Carregar exemplo</Button>
          <Button icon={<QuestionCircleOutlined />} onClick={() => setTourOpen(true)}>
            Como funciona
          </Button>
        </div>
      </S.Toolbar>

      <S.Layout>
        <S.Column>
          <S.Card id='sim-energy'>
            <S.CardTitle>Entrada de energia</S.CardTitle>
            <S.CardText>Corrente disponível do grupo (limite).</S.CardText>
            <Stepper
              big
              step={5}
              ariaLabel='limite do grupo'
              value={scenario.current_nominal_a}
              onChange={(value) => update({ current_nominal_a: value })}
            />
            <InputNumber
              style={{ width: '100%', marginTop: 8 }}
              min={0}
              max={2000}
              aria-label='Limite do grupo'
              value={scenario.current_nominal_a}
              onChange={(value) => update({ current_nominal_a: Number(value) || 0 })}
            />
          </S.Card>

          <S.Card id='sim-meter'>
            <S.CardTitle>Carga externa (medidor)</S.CardTitle>
            <S.CardText>O que o medidor enxerga além dos carregadores, por fase.</S.CardText>
            <S.Stepper>
              <span>Grupo com medidor</span>
              <Switch
                aria-label='Grupo com medidor'
                checked={Boolean(scenario.meter)}
                onChange={(checked) => update({ meter: checked ? emptyMeter() : null })}
              />
            </S.Stepper>
            {scenario.meter && (
              <>
                <S.Stepper>
                  <span>Medidor comunicando</span>
                  <Switch
                    aria-label='Medidor comunicando'
                    checked={scenario.meter.online}
                    onChange={(online) => scenario.meter && update({ meter: { ...scenario.meter, online } })}
                  />
                </S.Stepper>
                <div style={{ marginTop: 12 }}>
                  {GRID_PHASES.map((phase) => (
                    <Stepper
                      key={phase}
                      label={`Fase ${phase}`}
                      step={1}
                      ariaLabel={`carga externa da fase ${phase}`}
                      value={scenario.meter?.external_load_a[phase] || 0}
                      onChange={(value) => setExternalLoad(phase, value)}
                    />
                  ))}
                </div>
              </>
            )}
          </S.Card>
        </S.Column>

        <S.Column>
          <S.Card id='sim-stations'>
            <S.Toolbar style={{ marginBottom: 8 }}>
              <div>
                <S.CardTitle>Vagas de veículos elétricos</S.CardTitle>
                <S.CardText style={{ margin: 0 }}>
                  {scenario.charge_boxes.length} estação(ões), {chargingCount} carro(s) carregando
                </S.CardText>
              </div>
              <Button
                icon={<PlusOutlined />}
                disabled={scenario.charge_boxes.length >= MAX_STATIONS}
                onClick={() =>
                  update({ charge_boxes: [...scenario.charge_boxes, newChargeBox(scenario.charge_boxes.length)] })
                }
              >
                Estação
              </Button>
            </S.Toolbar>
            {withoutPhase.length > 0 && (
              <Alert
                style={{ marginBottom: 12 }}
                type='warning'
                showIcon
                message={`Sem fase cadastrada: ${withoutPhase.map((chargeBox) => chargeBox.code).join(', ')}`}
                description='Sem a ligação real, a estação é contada nas três fases e o Por Fases perde o ganho. Cadastre a fase/rotação no cadastro da estação.'
              />
            )}
            <S.StationsGrid>
              {scenario.charge_boxes.map((chargeBox) => (
                <StationEditor
                  key={chargeBox.key}
                  chargeBox={chargeBox}
                  onChange={(changed) => updateChargeBox(chargeBox.key, changed)}
                  onRemove={() =>
                    update({ charge_boxes: scenario.charge_boxes.filter((current) => current.key !== chargeBox.key) })
                  }
                />
              ))}
            </S.StationsGrid>
          </S.Card>

          <div id='sim-results'>
            <Spin spinning={loading && !result}>
              {error && (
                <Alert
                  style={{ marginBottom: 12 }}
                  type='error'
                  showIcon
                  message='Não foi possível simular agora. Tente de novo em instantes.'
                />
              )}
              {result && (
                <>
                  <CompareBanner results={result.algorithms} />
                  <S.ResultsGrid>
                    {result.algorithms.map((algorithm) => (
                      <AlgorithmResult
                        key={algorithm.algorithm}
                        result={algorithm}
                        securityA={result.security_a}
                        limitA={algorithm.limit_a ?? algorithm.phases[0]?.limit_a ?? scenario.current_nominal_a}
                      />
                    ))}
                  </S.ResultsGrid>
                </>
              )}
            </Spin>
          </div>
        </S.Column>
      </S.Layout>
    </S.Page>
  );
};

export default SmartChargingSimulator;
